import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import styles from './HealthHistory.module.css';
import MainPain from '../../../appointment/components/mainPain/MainPain';
import AnamneseHistory from '../../../appointment/components/anamneseHistory/AnamneseHistory';
import PatientHistory from '../../../appointment/components/patientHistory/PatientHistory';
import CranialAsymmetry from '../../../appointment/components/cranialAsymmetry/CranialAsymmetry';
import GrowthCurve from '../../../appointment/components/growthCurve/GrowthCurve';
import MotorMilestones from '../../../appointment/components/motorMilestones/MotorMilestones';
import Thermography from '../../../appointment/components/thermography/Thermography';
import Aims from '../../../appointment/components/aims/Aims';
import Notes from '../../../appointment/components/notes/Notes';
import BodyMeasurements from '../../../appointment/components/bodyMeasurements/BodyMeasurements';
import EvolutionPhotos from '../../../appointment/components/evolutionPhotos/EvolutionPhotos';
import { filterEvolutionPhotos } from '../../../appointment/modules/evolutionPhotos';
import BodyMap from '../../../appointment/components/bodyMap/BodyMap';
import MealPlan from '../../../appointment/components/mealPlan/MealPlan';
import ApplicationMap from '../../../appointment/components/applicationMap/ApplicationMap';
import ScaleHistory from '../../../appointment/components/scales/ScaleHistory';
import Diagnoses from '../../../appointment/components/diagnoses/Diagnoses';
import Documents from '../../../appointment/components/documents/Documents';
import { SCALES } from '../../../appointment/modules/scales';
import FullReportButton from '../../../appointment/components/fullReport/FullReportButton';
import { resolveVisibleModules } from '../../../appointment/modules/appointmentModules';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { GetPatientHealthAPI } from '../../../../api/API';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useParams } from 'react-router-dom';

/**
 * Seção Saúde da ficha: o prontuário inteiro em leitura (queixas, anamneses,
 * evoluções e os módulos que têm registro), com o relatório completo no
 * cabeçalho. A leitura é auditada: carrega uma vez por ficha aberta.
 */
const HealthHistory = () => {
    const { id: patientId } = useParams();
    const { GetPatientById, CompanyConfig, GetPatientAttachmentFile } = DataProviderInstance();
    const [loading, setLoading] = useState(true);
    const [healthData, setHealthData] = useState({
        complaints: [],
        anamneses: [],
        evolutions: [],
        cranialAsymmetry: [],
        growthMeasurements: [],
        motorMilestones: [],
        birthInfo: null,
        thermography: [],
        aimsAssessments: [],
        prescriptions: [],
        diagnoses: [],
        bodyMeasurements: [],
        bodyMap: [],
        mealPlans: [],
        applicationMaps: [],
        scaleAssessments: [],
        patientAttachments: [],
        notes: []
    });

    // Ficha já carregada: o StrictMode roda o efeito duas vezes e cada carga
    // grava uma leitura do prontuário na auditoria.
    const loadedIdRef = useRef(null);

    useEffect(() => {
        if (patientId && loadedIdRef.current !== patientId) {
            loadedIdRef.current = patientId;
            setLoading(true);
            GetPatientHealthAPI(patientId, (data) => {
                setHealthData(data);
                setLoading(false);
            }, (error) => {

                setLoading(false);
            });
        }
    }, [patientId]);

    let initialComplaints = [];

    if (healthData.complaints && healthData.complaints.length > 0) {
        initialComplaints = JSON.parse(healthData.complaints[0].list);
    }

    // Relatório do paciente: mesmos módulos e seções da tela de atendimento,
    // sem atendimento nem profissional (a ficha é do paciente, não da consulta)
    const patient = GetPatientById(patientId) || {};
    const reportData = { ...healthData, history: healthData.evolutions, cranialAsymetry: healthData.cranialAsymmetry };
    const reportContext = {
        data: reportData,
        patient,
        appointment: null,
        companyConfig: CompanyConfig,
        loaders: { GetPatientAttachmentFile },
    };

    function onChangeComplaintsHandler() { }
    function onAssesmentChangeHandler() { }
    function onEvolutionChangeHandler() { }
    function ReloadPage() { }

    const reportAction = loading ? null : (
        <FullReportButton
            modules={resolveVisibleModules([], reportData)}
            ctx={reportContext}
            patientName={patient.name}
            companyConfig={CompanyConfig}
            title="Relatório do paciente"
            subtitle={`Ficha de saúde · emitido em ${new Date().toLocaleDateString('pt-BR')}`}
        />
    );

    return (
        <div className={styles.column}>
            <ModuleCard
                title="Saúde"
                icon={<HealthAndSafetyOutlinedIcon />}
                description="Queixas, anamneses, evoluções e módulos registrados nos atendimentos"
                accent="pink"
                actions={reportAction}
            />

            {loading ? (
                <div className={styles.loading}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <MainPain
                        list={initialComplaints}
                        history={healthData.evolutions}
                        onChange={onChangeComplaintsHandler}
                        enabled={false}
                        patientName={patient.name}
                        companyConfig={CompanyConfig}
                    />

                    <AnamneseHistory
                        patientId={patientId}
                        currentDate={''}
                        list={healthData.anamneses}
                        onUpdated={ReloadPage}
                        enabled={false}
                        collapsible={true}
                        patientName={patient.name}
                    />

                    <PatientHistory
                        data={healthData.evolutions}
                        enabled={false}
                        onAssesmentChange={onAssesmentChangeHandler}
                        onEvolutionChange={onEvolutionChangeHandler}
                        collapsible={true}
                        patientName={patient.name}
                        companyConfig={CompanyConfig}
                    />

                    {healthData.notes && healthData.notes.length > 0 && (
                        <Notes
                            list={healthData.notes}
                            enabled={false}
                            collapsible={true}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                        />
                    )}

                    {healthData.bodyMeasurements && healthData.bodyMeasurements.length > 0 && (
                        <BodyMeasurements
                            data={healthData.bodyMeasurements}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            gender={patient.gender}
                            birthday={patient.birthday}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {filterEvolutionPhotos(healthData.patientAttachments).length > 0 && (
                        <EvolutionPhotos
                            attachments={healthData.patientAttachments}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.bodyMap && healthData.bodyMap.length > 0 && (
                        <BodyMap
                            data={healthData.bodyMap}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            gender={patient.gender}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.mealPlans && healthData.mealPlans.length > 0 && (
                        <MealPlan
                            data={healthData.mealPlans}
                            bodyMeasurements={healthData.bodyMeasurements}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.applicationMaps && healthData.applicationMaps.length > 0 && (
                        <ApplicationMap
                            data={healthData.applicationMaps}
                            products={[]}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            gender={patient.gender}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {SCALES.filter((scale) => (healthData.scaleAssessments || []).some((record) => record.scaleKey === scale.key)).map((scale) => (
                        <ScaleHistory key={scale.key} scale={scale} data={healthData.scaleAssessments} collapsible={true} />
                    ))}

                    {healthData.cranialAsymmetry && healthData.cranialAsymmetry.length > 0 && (
                        <CranialAsymmetry
                            data={healthData.cranialAsymmetry}
                            attachments={healthData.patientAttachments || []}
                            enabled={false}
                            collapsible={true}
                            patientName={patient.name || ''}
                            companyData={CompanyConfig}
                        />
                    )}

                    {healthData.growthMeasurements && healthData.growthMeasurements.length > 0 && (
                        <GrowthCurve
                            data={healthData.growthMeasurements}
                            cranial={healthData.cranialAsymmetry}
                            patientId={patientId}
                            birthday={patient.birthday}
                            gender={patient.gender}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.diagnoses && healthData.diagnoses.length > 0 && (
                        <Diagnoses
                            data={healthData.diagnoses}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.prescriptions && healthData.prescriptions.length > 0 && (
                        <Documents
                            title="Receitas emitidas"
                            patientData={patient}
                            list={[]}
                            prescriptions={healthData.prescriptions}
                            prescriptionProps={{ patientId, patientName: patient.name, readOnly: true }}
                            collapsible={true}
                        />
                    )}

                    {healthData.motorMilestones && healthData.motorMilestones.length > 0 && (
                        <MotorMilestones
                            data={healthData.motorMilestones}
                            birthInfo={healthData.birthInfo}
                            patientId={patientId}
                            birthday={patient.birthday}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.aimsAssessments && healthData.aimsAssessments.length > 0 && (
                        <Aims
                            data={healthData.aimsAssessments}
                            birthInfo={healthData.birthInfo}
                            patientId={patientId}
                            birthday={patient.birthday}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}

                    {healthData.thermography && healthData.thermography.length > 0 && (
                        <Thermography
                            data={healthData.thermography}
                            patientId={patientId}
                            patientName={patient.name}
                            companyConfig={CompanyConfig}
                            enabled={false}
                            collapsible={true}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default HealthHistory;
